import { useState, useEffect } from 'react'
import { useToast } from '../../components/Toast'
import { AppLayout } from '../../components/layouts/AppLayout'
import { Button } from '../../components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import {
  UsersThree,
  Plus,
  MagnifyingGlass,
  Trash,
  EnvelopeSimple,
  Clock,
  CheckCircle,
  XCircle,
  Buildings,
  Star,
  Spinner,
  Warning,
  X,
  ArrowClockwise,
  Prohibit,
  LockOpen,
} from '@phosphor-icons/react'
import {
  getShipperAgents,
  getShipperInvites,
  getBlockedAgents,
  cancelInvite,
  removeAgent,
  resendInvite,
  blockAgent,
  unblockAgent,
  ShipperAgent,
  ShipperAgentInvite,
  BlockedAgent,
} from '../../services/shipper-agents'
import { formatDateBR } from '../../lib/date'
import InviteAgentModal from '../../components/shipper/InviteAgentModal'

type TabType = 'agents' | 'invites' | 'blocked'

export default function AgentsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('agents')
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [agents, setAgents] = useState<ShipperAgent[]>([])
  const [invites, setInvites] = useState<ShipperAgentInvite[]>([])
  const [blockedAgentsList, setBlockedAgentsList] = useState<BlockedAgent[]>([])
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [removeConfirm, setRemoveConfirm] = useState<string | null>(null)
  const [cancelConfirm, setCancelConfirm] = useState<string | null>(null)
  const [blockConfirm, setBlockConfirm] = useState<string | null>(null)
  const [unblockConfirm, setUnblockConfirm] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [resendLoading, setResendLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const { showToast, ToastComponent } = useToast()

  // Load data
  const loadData = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const [agentsData, invitesData, blockedData] = await Promise.all([
        getShipperAgents(),
        getShipperInvites(),
        getBlockedAgents(),
      ])
      setAgents(agentsData)
      setInvites(invitesData)
      setBlockedAgentsList(blockedData)
    } catch (err: any) {
      console.error('Error loading agents data:', err)
      setError(err.response?.data?.error || 'Erro ao carregar dados')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [])

  // Filter agents by search term
  const filteredAgents = agents.filter(
    (agent) =>
      agent.agentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.agentTradingName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.agentDocument?.includes(searchTerm)
  )

  // Filter invites by search term
  const filteredInvites = invites.filter(
    (invite) =>
      invite.agentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invite.inviteEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      invite.inviteCompanyName?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Filter blocked agents by search term
  const filteredBlockedAgents = blockedAgentsList.filter(
    (blocked) =>
      blocked.agentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blocked.agentTradingName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      blocked.agentDocument?.includes(searchTerm)
  )

  // Handle remove agent
  const handleRemoveAgent = async (freightForwarderId: string) => {
    setActionLoading(freightForwarderId)
    try {
      await removeAgent(freightForwarderId)
      setAgents((prev) => prev.filter((a) => a.freightForwarderId !== freightForwarderId))
      setRemoveConfirm(null)
    } catch (err: any) {
      console.error('Error removing agent:', err)
      showToast(err.response?.data?.error || 'Erro ao remover agente', 'error')
    } finally {
      setActionLoading(null)
    }
  }

  // Handle cancel invite
  const handleCancelInvite = async (inviteId: string) => {
    setActionLoading(inviteId)
    try {
      await cancelInvite(inviteId)
      setInvites((prev) => prev.filter((i) => i.id !== inviteId))
      setCancelConfirm(null)
    } catch (err: any) {
      console.error('Error cancelling invite:', err)
      showToast(err.response?.data?.error || 'Erro ao cancelar convite', 'error')
    } finally {
      setActionLoading(null)
    }
  }

  // Handle resend invite
  const handleResendInvite = async (inviteId: string) => {
    setResendLoading(inviteId)
    try {
      const updatedInvite = await resendInvite(inviteId)
      // Update the invite in the list with new expiration date
      setInvites((prev) =>
        prev.map((i) =>
          i.id === inviteId ? { ...i, expiresAt: updatedInvite.expiresAt } : i
        )
      )
      showToast('Convite reenviado com sucesso!', 'success')
    } catch (err: any) {
      console.error('Error resending invite:', err)
      showToast(err.response?.data?.error || 'Erro ao reenviar convite', 'error')
    } finally {
      setResendLoading(null)
    }
  }

  // Handle block agent
  const handleBlockAgent = async (freightForwarderId: string) => {
    setActionLoading(freightForwarderId)
    try {
      const blocked = await blockAgent(freightForwarderId)
      // Remove from agents list and add to blocked list
      const agentToBlock = agents.find((a) => a.freightForwarderId === freightForwarderId)
      setAgents((prev) => prev.filter((a) => a.freightForwarderId !== freightForwarderId))
      if (agentToBlock) {
        setBlockedAgentsList((prev) => [{
          id: blocked.id,
          shipperId: blocked.shipperId,
          freightForwarderId: blocked.freightForwarderId,
          blockedAt: blocked.blockedAt as unknown as string,
          blockedBy: blocked.blockedBy,
          reason: blocked.reason,
          createdAt: blocked.createdAt as unknown as string,
          updatedAt: blocked.updatedAt as unknown as string,
          agentName: agentToBlock.agentName,
          agentTradingName: agentToBlock.agentTradingName,
          agentDocument: agentToBlock.agentDocument,
          agentEmail: agentToBlock.agentEmail,
        }, ...prev])
      }
      setBlockConfirm(null)
      showToast('Agente bloqueado com sucesso!', 'success')
    } catch (err: any) {
      console.error('Error blocking agent:', err)
      showToast(err.response?.data?.error || 'Erro ao bloquear agente', 'error')
    } finally {
      setActionLoading(null)
    }
  }

  // Handle unblock agent
  const handleUnblockAgent = async (freightForwarderId: string) => {
    setActionLoading(freightForwarderId)
    try {
      await unblockAgent(freightForwarderId)
      setBlockedAgentsList((prev) => prev.filter((b) => b.freightForwarderId !== freightForwarderId))
      setUnblockConfirm(null)
      showToast('Agente desbloqueado com sucesso!', 'success')
      // Reload data to get updated agents list
      loadData()
    } catch (err: any) {
      console.error('Error unblocking agent:', err)
      showToast(err.response?.data?.error || 'Erro ao desbloquear agente', 'error')
    } finally {
      setActionLoading(null)
    }
  }

  // Handle invite success
  const handleInviteSuccess = () => {
    loadData()
  }


  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <UsersThree size={28} className="text-emerald-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Meus Agentes</h1>
                <p className="text-sm text-gray-500">
                  Gerencie os agentes de carga vinculados
                </p>
              </div>
            </div>
            <Button
              onClick={() => setIsInviteModalOpen(true)}
              className="gap-2"
              style={{ backgroundColor: 'rgb(5, 150, 105)' }}
            >
              <Plus size={20} weight="bold" />
              Convidar Agente
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('agents')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'agents'
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Agentes Ativos ({agents.length})
          </button>
          <button
            onClick={() => setActiveTab('invites')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'invites'
                ? 'bg-emerald-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Convites Pendentes
            {invites.length > 0 && (
              <span
                className={`px-2 py-0.5 text-xs rounded-full ${
                  activeTab === 'invites'
                    ? 'bg-white text-emerald-600'
                    : 'bg-emerald-600 text-white'
                }`}
              >
                {invites.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('blocked')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'blocked'
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Agentes Bloqueados
            {blockedAgentsList.length > 0 && (
              <span
                className={`px-2 py-0.5 text-xs rounded-full ${
                  activeTab === 'blocked'
                    ? 'bg-white text-red-600'
                    : 'bg-red-600 text-white'
                }`}
              >
                {blockedAgentsList.length}
              </span>
            )}
          </button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <MagnifyingGlass
              size={20}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Buscar agente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <Warning size={24} className="text-red-500" />
            <p className="text-red-700">{error}</p>
            <button
              onClick={loadData}
              className="ml-auto text-red-600 hover:text-red-800 underline text-sm"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size={40} className="animate-spin text-emerald-600" />
          </div>
        ) : (
          <>
            {/* Agents Tab */}
            {activeTab === 'agents' && (
              <div className="space-y-4">
                {filteredAgents.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <UsersThree size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500 font-medium">
                        {searchTerm
                          ? 'Nenhum agente encontrado'
                          : 'Nenhum agente vinculado'}
                      </p>
                      {!searchTerm && (
                        <p className="text-sm text-gray-400 mt-1">
                          Convide agentes de carga para receber ofertas de frete
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  filteredAgents.map((agent) => (
                    <Card key={agent.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="pt-5 pb-4 px-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                              <Buildings size={20} className="text-emerald-600" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-gray-900">
                                  {agent.agentName}
                                </h3>
                                {agent.isPlatformAgent && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                                    <Star size={12} weight="fill" />
                                    Plataforma
                                  </span>
                                )}
                              </div>
                              {agent.agentTradingName && (
                                <p className="text-sm text-gray-500">
                                  {agent.agentTradingName}
                                </p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                {agent.agentDocument && (
                                  <span>CNPJ: {agent.agentDocument}</span>
                                )}
                                {agent.spreadPercentage != null && (
                                  <span>Spread: {Number(agent.spreadPercentage)}%</span>
                                )}
                                <span>Desde: {formatDateBR(agent.createdAt)}</span>
                              </div>
                            </div>
                          </div>

                          {/* Actions - Platform agents can be blocked but not
                              removed (Plane #34): blocking a platform agent
                              stops them from seeing this shipper's quotes. */}
                          <div className="flex items-center gap-2">
                            {blockConfirm === agent.freightForwarderId ? (
                              <div className="flex items-center gap-2 bg-orange-50 p-2 rounded-lg">
                                <span className="text-sm text-orange-700">Bloquear?</span>
                                <button
                                  onClick={() => handleBlockAgent(agent.freightForwarderId)}
                                  disabled={actionLoading === agent.freightForwarderId}
                                  className="px-3 py-1 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 disabled:opacity-50"
                                >
                                  {actionLoading === agent.freightForwarderId ? (
                                    <Spinner size={16} className="animate-spin" />
                                  ) : (
                                    'Sim'
                                  )}
                                </button>
                                <button
                                  onClick={() => setBlockConfirm(null)}
                                  className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                                >
                                  Nao
                                </button>
                              </div>
                            ) : removeConfirm === agent.freightForwarderId ? (
                              <div className="flex items-center gap-2 bg-red-50 p-2 rounded-lg">
                                <span className="text-sm text-red-700">Remover?</span>
                                <button
                                  onClick={() => handleRemoveAgent(agent.freightForwarderId)}
                                  disabled={actionLoading === agent.freightForwarderId}
                                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                                >
                                  {actionLoading === agent.freightForwarderId ? (
                                    <Spinner size={16} className="animate-spin" />
                                  ) : (
                                    'Sim'
                                  )}
                                </button>
                                <button
                                  onClick={() => setRemoveConfirm(null)}
                                  className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                                >
                                  Nao
                                </button>
                              </div>
                            ) : (
                              <>
                                {agent.isPlatformAgent && (
                                  <span className="text-xs text-gray-400 italic mr-1">
                                    Disponível automaticamente
                                  </span>
                                )}
                                <button
                                  onClick={() => setBlockConfirm(agent.freightForwarderId)}
                                  className="p-2 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
                                  title="Bloquear agente"
                                >
                                  <Prohibit size={20} />
                                </button>
                                {!agent.isPlatformAgent && (
                                  <button
                                    onClick={() => setRemoveConfirm(agent.freightForwarderId)}
                                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Remover agente"
                                  >
                                    <Trash size={20} />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}

            {/* Invites Tab */}
            {activeTab === 'invites' && (
              <div className="space-y-4">
                {filteredInvites.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <EnvelopeSimple size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500 font-medium">
                        {searchTerm
                          ? 'Nenhum convite encontrado'
                          : 'Nenhum convite pendente'}
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  filteredInvites.map((invite) => (
                    <Card key={invite.id} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                              <EnvelopeSimple size={24} className="text-blue-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                {invite.agentName ||
                                  invite.inviteCompanyName ||
                                  invite.inviteEmail}
                              </h3>
                              {invite.inviteEmail && !invite.freightForwarderId && (
                                <p className="text-sm text-gray-500">
                                  {invite.inviteEmail}
                                </p>
                              )}
                              {invite.inviteContactName && (
                                <p className="text-sm text-gray-500">
                                  Contato: {invite.inviteContactName}
                                </p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Clock size={14} />
                                  Enviado: {formatDateBR(invite.createdAt)}
                                </span>
                                <span className="flex items-center gap-1 text-amber-600">
                                  <Clock size={14} />
                                  Expira: {formatDateBR(invite.expiresAt)}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Status & Actions */}
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center gap-1 px-3 py-1 bg-yellow-100 text-yellow-700 text-sm font-medium rounded-full">
                              <Clock size={14} />
                              Aguardando
                            </span>

                            {cancelConfirm === invite.id ? (
                              <div className="flex items-center gap-2 bg-red-50 p-2 rounded-lg">
                                <span className="text-sm text-red-700">Cancelar?</span>
                                <button
                                  onClick={() => handleCancelInvite(invite.id)}
                                  disabled={actionLoading === invite.id}
                                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50"
                                >
                                  {actionLoading === invite.id ? (
                                    <Spinner size={16} className="animate-spin" />
                                  ) : (
                                    'Sim'
                                  )}
                                </button>
                                <button
                                  onClick={() => setCancelConfirm(null)}
                                  className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                                >
                                  Nao
                                </button>
                              </div>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleResendInvite(invite.id)}
                                  disabled={resendLoading === invite.id}
                                  className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
                                  title="Reenviar convite"
                                >
                                  {resendLoading === invite.id ? (
                                    <Spinner size={20} className="animate-spin" />
                                  ) : (
                                    <ArrowClockwise size={20} />
                                  )}
                                </button>
                                <button
                                  onClick={() => setCancelConfirm(invite.id)}
                                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Cancelar convite"
                                >
                                  <X size={20} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}

            {/* Blocked Agents Tab */}
            {activeTab === 'blocked' && (
              <div className="space-y-4">
                {filteredBlockedAgents.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Prohibit size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500 font-medium">
                        {searchTerm
                          ? 'Nenhum agente bloqueado encontrado'
                          : 'Nenhum agente bloqueado'}
                      </p>
                      {!searchTerm && (
                        <p className="text-sm text-gray-400 mt-1">
                          Agentes bloqueados nao poderao ver suas cotacoes
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  filteredBlockedAgents.map((blocked) => (
                    <Card key={blocked.id} className="hover:shadow-md transition-shadow border-l-4 border-l-red-500">
                      <CardContent className="pt-5 pb-4 px-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                              <Prohibit size={20} className="text-red-600" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">
                                {blocked.agentName}
                              </h3>
                              {blocked.agentTradingName && (
                                <p className="text-sm text-gray-500">
                                  {blocked.agentTradingName}
                                </p>
                              )}
                              <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                {blocked.agentDocument && (
                                  <span>CNPJ: {blocked.agentDocument}</span>
                                )}
                                <span className="text-red-600">
                                  Bloqueado em: {formatDateBR(blocked.blockedAt)}
                                </span>
                              </div>
                              {blocked.reason && (
                                <p className="text-sm text-gray-500 mt-1">
                                  Motivo: {blocked.reason}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2">
                            {unblockConfirm === blocked.freightForwarderId ? (
                              <div className="flex items-center gap-2 bg-green-50 p-2 rounded-lg">
                                <span className="text-sm text-green-700">Desbloquear?</span>
                                <button
                                  onClick={() => handleUnblockAgent(blocked.freightForwarderId)}
                                  disabled={actionLoading === blocked.freightForwarderId}
                                  className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50"
                                >
                                  {actionLoading === blocked.freightForwarderId ? (
                                    <Spinner size={16} className="animate-spin" />
                                  ) : (
                                    'Sim'
                                  )}
                                </button>
                                <button
                                  onClick={() => setUnblockConfirm(null)}
                                  className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                                >
                                  Nao
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setUnblockConfirm(blocked.freightForwarderId)}
                                className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Desbloquear agente"
                              >
                                <LockOpen size={20} />
                              </button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            )}
          </>
        )}
      </main>

      {/* Invite Modal */}
      <InviteAgentModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onSuccess={handleInviteSuccess}
      />

      {/* Toast */}
      {ToastComponent}
    </AppLayout>
  )
}
